/*
 * Module for a sortable, filterable List
 */

define([
  "marionette",
  "messaging/messenger",
  "modules/shared/sortableList/sortableListView",
  "modules/shared/sortableList/sortableListHead/sortableListHeadModule",
  "modules/shared/sortableList/sortableListBody/sortableListBodyModule",
  "modules/shared/sortableList/sortableListCounter/sortableListCounterModule",
  "modules/shared/sortableList/sortableListPagination/sortableListPaginationModule",
  "modules/shared/sortableList/sortableListPaginationLazyLoading/sortableListPaginationLazyLoadingModule",
  "modules/shared/sortableList/sortableListTitle/sortableListTitleModule",
  "modules/shared/sortableList/sortableListHeadSelector/sortableListHeadSelectorModule",
], function (
  Marionette,
  Messenger,
  SortableListView,
  SortableListHeadModule,
  SortableListBodyModule,
  SortableListCounterModule,
  SortableListPaginationModule,
  SortableListPaginationLazyLoadingModule,
  SortableListTitleModule,
  SortableListHeadSelectorModule,
) {
  const DEFAULT_CURRENT_PAGE = 1;
  const DEFAULT_RECORDS_PER_PAGE = 10;

  const SortableListModule = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "sortableList";
      this.region = options.region;
      this.headers = options.headers;
      this.recordsPerPage = options.recordsPerPage;
      this.title = options.title;
      this.downloadable = options.downloadable;
      this.downloadID = options.downloadID;
      this.hideControls = options.hideControls;
      this.hideHeader = options.hideHeader;
      this.extendedEvents = options.extendedEvents;
      this.namespace = options.namespace; //namespace for all triggered events
      if (this.namespace) {
        this.namespacePrefix = this.namespace + ":";
      } else {
        this.namespacePrefix = "";
      }
      this.doNotResetCollection = options.doNotResetCollection;
      this.lazyLoading = options.lazyLoading;
      this._setUpVents();
    },
    show: function (collection) {
      if (this.lazyLoading) {
        this.model = collection;
        this.collection = collection.getDataCollection();
        this.totalRecordsModel = collection.historyCount();
      } else {
        this.collection = collection;
      }
      this.updateModal();
    },
    updateModal: function () {
      //reset collection only if we want to remove filtering or checkboxes from the models (default is to reset)
      if (!this.doNotResetCollection && !this.lazyLoading) {
        this._resetCollection(this.collection);
      }
      this.layout = new SortableListView.sortableListLayout();
      this.counter = this.addSubModule(
        new SortableListCounterModule({ region: this.layout.counter, namespace: this.namespace }),
      );
      if (this.recordsPerPage) {
        if (!this.lazyLoading) {
          this.pagination = this.addSubModule(
            new SortableListPaginationModule({
              region: this.layout.pagination,
              recordsPerPage: this.recordsPerPage,
              currentPage: DEFAULT_CURRENT_PAGE,
              namespace: this.namespace,
            }),
          );
        } else {
          this.pagination = this.addSubModule(
            new SortableListPaginationLazyLoadingModule({
              region: this.layout.pagination,
              recordsPerPage: this.recordsPerPage,
              currentPage: DEFAULT_CURRENT_PAGE,
              namespace: this.namespace,
              totalRecordsModel: this.totalRecordsModel,
            }),
          );
        }
      }

      if (this.title) {
        if (!this.downloadable) this.downloadable = false;
        this.titleModule = this.addSubModule(
          new SortableListTitleModule({
            region: this.layout.titleModule,
            title: this.title,
            headers: this.headers,
            hideHeader: this.hideHeader,
            downloadable: this.downloadable,
            downloadID: this.downloadID,
          }),
        );
      }
      this.head = this.addSubModule(
        new SortableListHeadModule({
          region: this.layout.head,
          headers: this.headers,
          namespace: this.namespace,
        }),
      );
      this.headSelector = this.addSubModule(
        new SortableListHeadSelectorModule({
          region: this.layout.headSelector,
          headers: this.headers,
          namespace: this.namespace,
        }),
      );
      this.body = this.addSubModule(
        new SortableListBodyModule({
          region: this.layout.body,
          headers: this.headers,
          paginationFilter: this.pagination ? this.pagination.paginationFilter() : null,
          extendedEvents: this.extendedEvents,
          namespace: this.namespace,
        }),
      ); //pass headers objects to help create row template

      this.layout.on(
        "show",
        function () {
          this._showSubViews(this.collection);
        },
        this,
      );

      this.showRegion(this.layout, {
        loading: true,
        entity: [this.collection, this.totalRecordsModel],
      });
    },
    updateBodyOnNext: async function (_collection, currentPage, recordsPerPage) {
      this.collection = await this.model.pagination(currentPage, recordsPerPage);
      this.body.update(this.collection);
    },
    _hasCheckboxField: function () {
      //check all headers for a checkbox field
      if (this.headers) {
        for (let i = 0; i < this.headers.length; i++) {
          if (this.headers.at(i).get("checkbox")) {
            return true;
          }
        }
      }
      return false;
    },
    _showSubViews: function (collection) {
      if (this.hideControls && !this.pagination) {
        //hide controls area if true and if not using pagination
        this.layout.hideControls();
      }
      //check if the list has a checkbox field.  If it does, show the counter module
      //if it doesn't, do not show it and let the pagination module take up the full row width
      if (this._hasCheckboxField()) {
        this.counter.show();
      } else {
        this.layout.removeCounter();
      }
      if (this.pagination) {
        if (!this.lazyLoading) {
          this.pagination.show(collection);
        } else {
          this.pagination.show(this.collection, collection);
        }
      }
      if (this.title) {
        this.titleModule.show(this.collection);
        this.layout.showTitle();
        this.layout.handleResize();
      }
      //hide the headers if this.hideHeader is true
      if (this.hideHeader) {
        this.layout.hideHeaders();
      }
      this.head.show(this.collection);
      this.headSelector.show();
      this.body.show(this.collection);
      this.head.defaultSort(); //sort by defaultSort column, or by leftmost sortable column if no defaultSort column exists
    },
    _resetCollection: function (collection) {
      //set checked attribute and filteredOut attribute to false for all models
      //won't do anything if collection is still being fetched from the server, but these values
      //are the model defaults, so that's ok.
      collection.each(function (model) {
        model.checked = false;
        model.filteredOut = false;
      });
    },
    _setUpVents: function () {
      // Listen for pagination events
      Messenger.vent.on(this.namespacePrefix + "pagination:navigate", this.updateBodyOnNext, this);
    },
  });

  SortableListModule.DEFAULT_CURRENT_PAGE = DEFAULT_CURRENT_PAGE;
  SortableListModule.DEFAULT_RECORDS_PER_PAGE = DEFAULT_RECORDS_PER_PAGE;

  return SortableListModule;
});
